import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ClipboardList, CheckSquare, Square, Scissors, Hammer, Flame as Weld, Wrench, PaintBucket, HardHat, Clock3, Truck, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";
import InstallPWAButton from "@/components/InstallPWAButton";
import WhatsAppMigrationWidget from "@/components/WhatsAppMigrationWidget";

const LOGO = "/logo.webp";

const STATUS = [
  { key: "pendiente", label: "Pendiente", color: "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10" },
  { key: "en_proceso", label: "En proceso", color: "text-blue-400 border-blue-500/40 bg-blue-500/10" },
  { key: "terminada", label: "Terminada", color: "text-green-400 border-green-500/40 bg-green-500/10" },
  { key: "lista_para_entrega", label: "Lista p/ entregar", color: "text-purple-400 border-purple-500/40 bg-purple-500/10" },
  { key: "entregada", label: "Entregada", color: "text-neutral-400 border-neutral-700 bg-neutral-800/50" },
  { key: "colocada", label: "Colocada", color: "text-cyan-400 border-cyan-500/40 bg-cyan-500/10" },
];

// Forward-only options the operario can pick from a given status — the empresa is the
// only one who can move an order from "terminada" to "lista_para_entrega" (dos carriles).
const OPERARIO_STATUS_OPTIONS = {
  pendiente: ["pendiente", "en_proceso", "terminada"],
  en_proceso: ["en_proceso", "terminada"],
};

const TASK_ICONS = { corte: Scissors, armado: Hammer, soldadura: Weld, instalacion: Wrench, pintura: PaintBucket, otro: HardHat };

const CUT_COLORS = ["#E53935", "#FFFFFF", "#B71C1C", "#FFCDD2", "#D32F2F", "#F5F5F5", "#8B0000", "#FF8A80", "#C62828", "#FFEBEE"];

const groupBarCuts = (cuts) => {
  const groups = [];
  const index = {};
  cuts.forEach((c) => {
    const key = `${c.label}__${c.length}`;
    if (!(key in index)) { index[key] = groups.length; groups.push({ label: c.label, length: c.length, qty: 0 }); }
    groups[index[key]].qty += 1;
  });
  return groups;
};

export default function Operario() {
  const [orders, setOrders] = useState([]);
  const [cutPlans, setCutPlans] = useState([]);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("gr_user") || "{}");

  const load = () => {
    api.get("/orders", { params: { mine: true } }).then((r) => setOrders(r.data)).catch(() => {});
    api.get("/cuts/plans").then((r) => setCutPlans(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const logout = () => {
    localStorage.removeItem("gr_token");
    localStorage.removeItem("gr_user");
    navigate("/login");
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/orders/${id}`, { status }); load(); toast.success("Estado actualizado"); } catch (e) { toast.error(apiError(e)); }
  };

  const toggleTask = async (orderId, taskId) => {
    try { await api.patch(`/orders/${orderId}/tasks/${taskId}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const setCutStatus = async (planId, status) => {
    try { await api.patch(`/cuts/plans/${planId}`, { status }); load(); toast.success("Estado del corte actualizado"); } catch (e) { toast.error(apiError(e)); }
  };

  const active = orders.filter((o) => o.status === "pendiente" || o.status === "en_proceso");
  const waiting = orders.filter((o) => o.status === "terminada");
  const readyToClose = orders.filter((o) => o.status === "lista_para_entrega");
  const done = orders.filter((o) => o.status === "entregada" || o.status === "colocada");
  const myOrderIds = new Set(orders.map((o) => o.id));
  const myCuts = cutPlans.filter((p) => p.assigned_to === user.id || (p.work_order_id && myOrderIds.has(p.work_order_id)));

  const CutPlanCard = ({ p }) => {
    const colors = {};
    let nextColor = 0;
    const colorForCut = (label) => {
      if (!colors[label]) colors[label] = CUT_COLORS[nextColor++ % CUT_COLORS.length];
      return colors[label];
    };
    return (
      <div className="bg-[#171717] border border-[#F2B90D]/30 rounded-md p-4" data-testid={`my-cut-plan-${p.id}`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="font-heading font-bold text-sm">{p.name}</p>
            <p className="text-[11px] text-neutral-500 font-mono mt-0.5">{p.result.total_bars} barras · {p.result.total_pieces} cortes · {p.result.efficiency_pct}% aprovechamiento</p>
          </div>
          <select value={p.status || "pendiente"} onChange={(e) => setCutStatus(p.id, e.target.value)}
            className="h-8 bg-neutral-900 border border-neutral-700 rounded-sm px-2 text-[11px] uppercase font-bold shrink-0" data-testid={`my-cut-status-${p.id}`}>
            <option value="pendiente">Pendiente</option>
            <option value="en_proceso">En proceso</option>
            <option value="terminado">Terminado</option>
          </select>
        </div>
        <div className="space-y-2">
          {p.result.bars.map((bar) => (
            <div key={bar.index}>
              <div className="flex justify-between text-[10px] text-neutral-500 mb-1">
                <span className="font-mono">Barra {bar.index} · {p.result.stock_length} mm</span>
                <span>Sobra <span className="font-mono text-neutral-300">{bar.waste} mm</span></span>
              </div>
              <div className="flex h-8 rounded-sm overflow-hidden border border-neutral-700 bg-neutral-900">
                {bar.cuts.map((cut, ci) => (
                  <div key={ci} style={{ width: `${(cut.length / p.result.stock_length) * 100}%`, background: colorForCut(cut.label) }}
                    className="flex flex-col items-center justify-center text-[9px] leading-tight font-mono font-bold text-black/80 border-r border-black/30 overflow-hidden"
                    title={`${cut.label}: ${cut.length}mm`}>
                    <span>{cut.length}</span><span className="max-w-full truncate px-0.5">{cut.label}</span>
                  </div>
                ))}
                <div style={{ width: `${(bar.waste / p.result.stock_length) * 100}%` }} className="bg-neutral-800" />
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {groupBarCuts(bar.cuts).map((g, gi) => (
                  <span key={gi} className="text-[10px] font-mono bg-neutral-900 border border-neutral-700 rounded-sm px-1.5 py-0.5 text-neutral-300 whitespace-nowrap">
                    {g.label}: {g.length}mm{g.qty > 1 ? ` ×${g.qty}` : ""}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const OrderCard = ({ o }) => {
    const st = STATUS.find((s) => s.key === o.status) || STATUS[0];
    const tasks = o.tasks || [];
    const doneCount = tasks.filter((t) => t.done).length;
    const plans = cutPlans.filter((p) => p.work_order_id === o.id && !p.assigned_to);
    const labelColors = {};
    let colorIdx = 0;
    const colorFor = (label) => {
      if (!labelColors[label]) labelColors[label] = CUT_COLORS[colorIdx++ % CUT_COLORS.length];
      return labelColors[label];
    };
    return (
      <div className="bg-[#171717] border border-neutral-800 rounded-md p-5" data-testid={`op-order-card-${o.id}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-[#F2B90D] font-bold">{o.number}</span>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm border ${st.color}`}>{st.label}</span>
          </div>
          {OPERARIO_STATUS_OPTIONS[o.status] && (
            <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)}
              className="h-8 bg-neutral-900 border border-neutral-800 rounded-sm px-2 text-xs uppercase font-bold" data-testid={`op-order-status-${o.id}`}>
              {OPERARIO_STATUS_OPTIONS[o.status].map((key) => {
                const s = STATUS.find((x) => x.key === key);
                return <option key={key} value={key}>{s.label}</option>;
              })}
            </select>
          )}
          {o.status === "lista_para_entrega" && (
            <div className="flex gap-2">
              <button onClick={() => setStatus(o.id, "entregada")}
                className="flex items-center gap-1 bg-neutral-800 hover:bg-neutral-700 rounded-sm h-8 px-3 text-xs font-heading uppercase font-bold" data-testid={`op-order-deliver-${o.id}`}>
                <Truck className="w-3.5 h-3.5" /> Entregada
              </button>
              <button onClick={() => setStatus(o.id, "colocada")}
                className="flex items-center gap-1 bg-cyan-600 hover:bg-cyan-500 text-black rounded-sm h-8 px-3 text-xs font-heading uppercase font-bold" data-testid={`op-order-install-${o.id}`}>
                <MapPin className="w-3.5 h-3.5" /> Colocada
              </button>
            </div>
          )}
        </div>
        {o.status === "terminada" && (
          <p className="flex items-center gap-1.5 text-[11px] text-neutral-500 mt-2">
            <Clock3 className="w-3.5 h-3.5" /> Esperando que la empresa coordine la entrega o colocación
          </p>
        )}
        <h3 className="font-heading font-bold text-base mt-2">{o.title}</h3>
        <p className="text-xs text-neutral-500 mt-0.5">Cliente: {o.client_name}</p>
        {o.notes && <p className="text-xs text-neutral-400 mt-2 bg-neutral-900 rounded-sm p-2 whitespace-pre-wrap">{o.notes}</p>}
        {tasks.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase font-bold tracking-wider text-neutral-400">Tareas</p>
              <span className="text-[11px] font-mono text-neutral-500">{doneCount}/{tasks.length}</span>
            </div>
            <div className="h-1.5 bg-neutral-800 rounded-full mb-3 overflow-hidden">
              <div className="h-full bg-[#F2B90D] transition-all" style={{ width: `${tasks.length ? (doneCount / tasks.length) * 100 : 0}%` }} />
            </div>
            <div className="space-y-2">
              {tasks.map((t) => {
                const Icon = TASK_ICONS[t.type] || HardHat;
                return (
                  <button key={t.id} onClick={() => toggleTask(o.id, t.id)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-sm border text-left transition-colors ${t.done ? "border-green-500/30 bg-green-500/5" : "border-neutral-800 bg-neutral-900 hover:bg-neutral-800"}`}
                    data-testid={`op-task-${t.id}`}>
                    {t.done ? <CheckSquare className="w-4 h-4 text-green-400 shrink-0" /> : <Square className="w-4 h-4 text-neutral-500 shrink-0" />}
                    <Icon className="w-3.5 h-3.5 text-[#F2B90D] shrink-0" />
                    <span className={`text-sm ${t.done ? "line-through text-neutral-500" : "text-neutral-200"}`}>{t.description}</span>
                    <span className="ml-auto text-[10px] uppercase font-bold text-neutral-600">{t.type}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {plans.length > 0 && (
          <div className="mt-4 pt-3 border-t border-neutral-800 space-y-3">
            <p className="text-[11px] uppercase font-bold tracking-wider text-neutral-400 flex items-center gap-1.5">
              <Scissors className="w-3.5 h-3.5 text-[#F2B90D]" /> Plan de corte
            </p>
            {plans.map((p) => (
              <div key={p.id} data-testid={`op-cut-plan-${p.id}`}>
                <div className="flex items-center justify-between text-xs text-neutral-400 mb-2">
                  <span className="font-medium text-neutral-200">{p.name}</span>
                  <span className="font-mono">{p.result.total_bars} barras · {p.result.efficiency_pct}% aprov.</span>
                </div>
                <div className="space-y-1.5">
                  {p.result.bars.map((bar) => (
                    <div key={bar.index}>
                      <div className="flex justify-between text-[10px] text-neutral-500 mb-0.5">
                        <span className="font-mono">Barra {bar.index} · {p.result.stock_length}mm</span>
                        <span>Sobrante: <span className="font-mono text-neutral-300">{bar.waste}mm</span></span>
                      </div>
                      <div className="flex h-7 rounded-sm overflow-hidden border border-neutral-700 bg-neutral-900">
                        {bar.cuts.map((cut, ci) => (
                          <div key={ci} style={{ width: `${(cut.length / p.result.stock_length) * 100}%`, background: colorFor(cut.label) }}
                            className="flex items-center justify-center text-[9px] font-mono font-bold text-black/80 border-r border-black/30 overflow-hidden whitespace-nowrap"
                            title={`${cut.label}: ${cut.length}mm`}>
                            {cut.length}
                          </div>
                        ))}
                        <div style={{ width: `${(bar.waste / p.result.stock_length) * 100}%` }} className="bg-neutral-800" title={`Sobrante: ${bar.waste}mm`} />
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {groupBarCuts(bar.cuts).map((g, gi) => (
                          <span key={gi} className="text-[10px] font-mono bg-neutral-900 border border-neutral-700 rounded-sm px-1.5 py-0.5 text-neutral-300 whitespace-nowrap">
                            {g.label}: {g.length}mm{g.qty > 1 ? ` ×${g.qty}` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="operario-dashboard">
      <header className="sticky top-0 z-40 bg-[#111] border-b border-neutral-800">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="GR" className="h-9 w-9 object-contain rounded-sm bg-white p-0.5" />
            <div className="leading-tight">
              <p className="font-heading font-bold uppercase text-sm">Mis trabajos</p>
              <p className="text-[11px] text-neutral-500">{user.name || user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <InstallPWAButton compact />
            <NotificationBell />
            <button onClick={logout} className="p-2 rounded-sm hover:bg-neutral-800 text-neutral-400 hover:text-white" data-testid="op-logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <WhatsAppMigrationWidget hideWhenClosed />
        <section>
          <h2 className="font-heading font-bold uppercase text-sm tracking-wider text-[#F2B90D] mb-3 flex items-center gap-2">
            <Scissors className="w-4 h-4" /> Mis cortes ({myCuts.length})
          </h2>
          {myCuts.length === 0 && (
            <div className="bg-[#111] border border-dashed border-neutral-800 rounded-md p-6 text-center text-neutral-500 text-sm">
              No tenés planes de corte asignados.
            </div>
          )}
          <div className="space-y-4">{myCuts.map((p) => <CutPlanCard key={p.id} p={p} />)}</div>
        </section>
        <section>
          <h2 className="font-heading font-bold uppercase text-sm tracking-wider text-neutral-400 mb-3 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#F2B90D]" /> Órdenes activas ({active.length})
          </h2>
          {active.length === 0 && (
            <div className="bg-[#111] border border-dashed border-neutral-800 rounded-md p-8 text-center text-neutral-500 text-sm">
              No tenés órdenes de trabajo asignadas por el momento.
            </div>
          )}
          <div className="space-y-4">{active.map((o) => <OrderCard key={o.id} o={o} />)}</div>
        </section>
        {readyToClose.length > 0 && (
          <section>
            <h2 className="font-heading font-bold uppercase text-sm tracking-wider text-purple-400 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4" /> Para entregar o colocar ({readyToClose.length})
            </h2>
            <div className="space-y-4">{readyToClose.map((o) => <OrderCard key={o.id} o={o} />)}</div>
          </section>
        )}
        {waiting.length > 0 && (
          <section>
            <h2 className="font-heading font-bold uppercase text-sm tracking-wider text-neutral-400 mb-3 flex items-center gap-2">
              <Clock3 className="w-4 h-4" /> Esperando a la empresa ({waiting.length})
            </h2>
            <div className="space-y-4 opacity-80">{waiting.map((o) => <OrderCard key={o.id} o={o} />)}</div>
          </section>
        )}
        {done.length > 0 && (
          <section>
            <h2 className="font-heading font-bold uppercase text-sm tracking-wider text-neutral-400 mb-3">Entregadas / Colocadas ({done.length})</h2>
            <div className="space-y-4 opacity-70">{done.map((o) => <OrderCard key={o.id} o={o} />)}</div>
          </section>
        )}
      </main>
    </div>
  );
}
